import {
  Component,
  Input,
  Output,
  ContentChildren,
  QueryList,
  ChangeDetectionStrategy,
  AfterContentInit,
  ChangeDetectorRef,
  SimpleChanges,
  EventEmitter,
  OnChanges
} from '@angular/core';
import { DragulaService } from 'ng2-dragula/ng2-dragula';
import { SkyGridColumnComponent } from './grid-column.component';
import { SkyGridColumnModel } from './grid-column.model';
import { ListItemModel } from '../list/state';
import { SkyGridAdapterService } from './grid-adapter.service';

import { ListSortFieldSelectorModel } from '../list/state';

import { BehaviorSubject } from 'rxjs/BehaviorSubject';

import { Observable } from 'rxjs/Observable';


export class SkyGridComponent implements AfterContentInit, OnChanges {

  
  public selectedColumnIds: Array<string>;

  
  public fit: string = 'width';

  
  public width: number;

  
  public height: number;

  
  public data: Array<any>;

  
  public columns: Array<SkyGridColumnModel>;

  
  public hasToolbar: boolean = false;

  
  public sortField: ListSortFieldSelectorModel;

  
  public selectedColumnIdsChange = new EventEmitter<Array<string>>();

  
  public sortFieldChange = new EventEmitter<ListSortFieldSelectorModel>();

  public displayedColumns: Array<SkyGridColumnModel> = new Array<SkyGridColumnModel>();

  public items: Array<any> = new Array<any>();

  public currentSortField: BehaviorSubject<ListSortFieldSelectorModel>
    = new BehaviorSubject<ListSortFieldSelectorModel>({ fieldSelector: '', descending: false });

  
  private columnComponents: QueryList<SkyGridColumnComponent>;

  constructor(
    private dragulaService: DragulaService,
    private ref: ChangeDetectorRef,
    private gridAdapter: SkyGridAdapterService
  ) {}

  public ngAfterContentInit() {
    if (this.columnComponents.length !== 0 || this.columns !== undefined) {
      /* istanbul ignore else */
      /* sanity check */
      if (this.columnComponents.length > 0) {
        this.getColumnsFromComponent();
      }

      this.transformData();

      this.setDisplayedColumns(true);
    }

    this.columnComponents.changes.subscribe((columnComponents) => {
      this.getColumnsFromComponent();
      this.setDisplayedColumns(true);
      this.ref.markForCheck();
    });

    this.gridAdapter.initializeDragAndDrop(
        this.dragulaService,
        (selectedColumnIds: Array<string>) => {
          this.onHeaderDrop(selectedColumnIds);
        }
      );
  }

  // Do an ngOnChanges where changes to selectedColumnIds and data are watched
  public ngOnChanges(changes: SimpleChanges) {
    if (changes['columns'] && this.columns) {
      this.setDisplayedColumns(true);
    } else if (changes['selectedColumnIds'] && this.columns) {
      this.setDisplayedColumns();
    }

    if (changes['data'] && this.data) {
      this.transformData();
    }

    if (changes['sortField']) {
      this.setSortHeaders();
    }
  }

  public sortByColumn(column: SkyGridColumnModel) {
    if (column.isSortable) {
      this.currentSortField
      .take(1)
      .map(field => {
        let selector = {
          fieldSelector: column.field,
          descending: true
        };

        if (field && field.fieldSelector === column.field && field.descending) {
          selector = {
            fieldSelector: column.field,
            descending: false
          };
        }
        this.sortFieldChange.emit(selector);
        this.currentSortField.next(selector);
      })
      .subscribe();
    }
  }

  public getSortDirection(columnField: string): Observable<string> {
    return this.currentSortField
      .distinctUntilChanged()
      .map(field => {
        return field.fieldSelector === columnField ?
          (field.descending ? 'desc' : 'asc') : undefined;
      });
  }

  private onHeaderDrop(newColumnIds: Array<string>) {
     // update selected columnIds
      this.selectedColumnIds = newColumnIds;
      this.selectedColumnIdsChange.emit(newColumnIds);

      // set new displayed columns
      this.displayedColumns = this.selectedColumnIds.map(
        columnId => this.columns.filter(column => column.id === columnId)[0]
      );

      // mark for check because we are using ChangeDetectionStrategy.onPush
      this.ref.markForCheck();
  }

  private setDisplayedColumns(respectHidden: boolean = false) {
    if (this.selectedColumnIds !== undefined) {
      // setup displayed columns
      this.displayedColumns = this.selectedColumnIds.map(
        columnId => this.columns.filter(column => column.id === columnId)[0]
      );
    } else if (respectHidden) {
      this.displayedColumns = this.columns.filter(column => {
        return !column.hidden;
      });
    } else {
      this.displayedColumns = this.columns;
    }
  }

  private transformData() {
    // Transform data into object with id and data properties
    if (this.data.length > 0 && this.data[0].id && !this.data[0].data) {
      this.items = this.data.map(item => new ListItemModel(item.id, item));
    } else {
      this.items = this.data;
    }
  }

  private setSortHeaders() {
    this.currentSortField.next(this.sortField || { fieldSelector: '', descending: false });
  }

  private getColumnsFromComponent() {
     this.columns = this.columnComponents.map(columnComponent => {
        return new SkyGridColumnModel(columnComponent.template, columnComponent);
      });
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'sky-grid',
  template: `<div class="sky-grid">
  <div class="sky-grid-table-container" [style.height.px]="height" [style.width.px]="width">
    <table
      class="sky-grid-table"
      [ngClass]="{ 'sky-grid-fit': fit !== 'scroll', 'sky-grid-has-toolbar': hasToolbar }">
      <thead>
        <tr dragula="sky-grid-heading">
          <th
            scope="col"
            class="sky-grid-heading"
            *ngFor="let column of displayedColumns; let last = last"
            [style.width.px]="last ? null : column.width"
            [attr.sky-cmp-id]="column.id || column.field"
            [ngClass]="{ 'sky-grid-header-locked': column.locked }"
            (click)="sortByColumn(column)">
            {{column.heading}}
            <i
              class="fa sky-grid-heading-sort"
              [ngStyle]="{'visibility: hidden': (getSortDirection(column.field) | async)}"
              [ngClass]="{ 'fa-caret-up': (getSortDirection(column.field) | async) === 'asc', 'fa-caret-down': (getSortDirection(column.field) | async) === 'desc' }"
            >
            </i>
          </th>
        </tr>
      </thead>
      <tbody class="sky-grid-tbody">
        <tr
          class="sky-grid-row"
          *ngFor="let item of items"
          [attr.sky-cmp-id]="item.id">
          <td
            class="sky-grid-cell"
            *ngFor="let column of displayedColumns; let last = last; let i = index">
            <sky-grid-cell
              [template]="column.template || defaultCellTemplate"
              [fieldSelector]="column.field"
              [item]="item"
              [columnId]="column.id"
              [attr.sky-cmp-id]="column.id || column.field"
              [style.width.px]="last ? null : column.width">
            </sky-grid-cell>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</div>

<ng-template #defaultCellTemplate let-row="row" let-value="value">{{value}}</ng-template>
`,
  styles: [`.sky-grid{position:relative;display:block}.sky-grid-table-container{overflow:auto}.sky-grid-table{position:relative;table-layout:fixed;border-collapse:collapse;margin:0;font-size:15px;min-width:100%}.sky-grid-table.sky-grid-fit{width:100%}.sky-grid-tbody{background-color:#fff}.sky-grid-row{border-bottom:1px dotted #cdcfd2}.sky-grid-row:nth-child(odd){background-color:#fbfbfb}.sky-grid-row .sky-grid-cell{padding:0}.sky-grid-heading{border-top:1px solid #cdcfd2;border-bottom:1px solid #cdcfd2;border-left:1px solid #cdcfd2;border-right:1px solid #cdcfd2;color:#686c73;font-weight:400;font-size:15px;border-right-width:0px;cursor:pointer;background-color:#fff;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;padding:8px;text-align:left;-webkit-user-select:none;-webkit-tap-highlight-color:transparent;-moz-user-select:none;-ms-user-select:none;user-select:none}.sky-grid-heading:first-child{border-left:1px solid transparent}.sky-grid-heading.sky-grid-header-dragging{padding:8px;background-color:#eeeeef}.sky-grid-has-toolbar .sky-grid-heading{border-top-width:0px}.sky-grid-heading-sort{min-width:8px}
`],
  viewProviders: [ DragulaService ],
  providers: [
    SkyGridAdapterService
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: DragulaService, },
{type: ChangeDetectorRef, },
{type: SkyGridAdapterService, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'selectedColumnIds': [{ type: Input },],
'fit': [{ type: Input },],
'width': [{ type: Input },],
'height': [{ type: Input },],
'data': [{ type: Input },],
'columns': [{ type: Input },],
'hasToolbar': [{ type: Input },],
'sortField': [{ type: Input },],
'selectedColumnIdsChange': [{ type: Output },],
'sortFieldChange': [{ type: Output },],
'columnComponents': [{ type: ContentChildren, args: [SkyGridColumnComponent, {descendants: true}, ] },],
};
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
